import React from 'react';
import { useNavigate } from 'react-router-dom';
import CollapsibleSection from './CollapsibleSection';

// Icons
const AlertIcon = () => (
  <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

export interface CriticalTask {
  id: string;
  reference: string;
  taskType: 'BOOKING_INSTRUCTION' | 'SHIPPING_INSTRUCTION' | 'CONTRACT_SIGNING' | 'PENDING_INVOICE' | string;
  priority: 'CRITICAL' | 'URGENT' | 'NORMAL';
  supplier: string;
  buyer: string;
  dueDate: string;
  timeRemaining: string;
  isOverdue: boolean;
}

interface CriticalActionsSectionProps {
  tasks: CriticalTask[];
  onSendRequest?: (task: CriticalTask) => void;
  onSearchGmail?: (task: CriticalTask) => void;
}

const formatTaskType = (taskType: string): string => {
  const types: Record<string, string> = {
    'BOOKING_INSTRUCTION': 'Booking',
    'SHIPPING_INSTRUCTION': 'Shipping SI',
    'CONTRACT_SIGNING': 'Signing',
    'PENDING_INVOICE': 'Invoice Review',
  };
  return types[taskType] || taskType;
};

const getTaskTypeClass = (taskType: string): string => {
  const classes: Record<string, string> = {
    'BOOKING_INSTRUCTION': 'booking',
    'SHIPPING_INSTRUCTION': 'shipping',
    'CONTRACT_SIGNING': 'signing',
    'PENDING_INVOICE': 'invoice',
  };
  return classes[taskType] || '';
};

const CriticalActionsSection: React.FC<CriticalActionsSectionProps> = ({
  tasks,
  onSendRequest,
  onSearchGmail,
}) => {
  const navigate = useNavigate();

  const handleReferenceClick = (reference: string) => {
    navigate(`/contracts/${reference}`);
  };

  if (tasks.length === 0) {
    return (
      <CollapsibleSection
        title="Critical Actions"
        count={0}
        icon={<AlertIcon />}
        variant="default"
      >
        <div className="empty-state">
          <div className="empty-state-icon">✓</div>
          <div className="empty-state-text">No critical actions at this time</div>
        </div>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title="Critical Actions"
      count={tasks.length}
      icon={<AlertIcon />}
      variant="critical"
    >
      <div className="table-wrapper">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Task</th>
              <th>Supplier</th>
              <th>Buyer</th>
              <th>Due Date</th>
              <th>Time Left</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr 
                key={task.id} 
                className={`priority-${task.priority.toLowerCase()}`}
              >
                <td>
                  <span 
                    className="ref-link"
                    onClick={() => handleReferenceClick(task.reference)}
                  >
                    {task.reference}
                  </span>
                </td>
                <td>
                  <span className={`task-type ${getTaskTypeClass(task.taskType)}`}>
                    {formatTaskType(task.taskType)}
                  </span>
                </td>
                <td>{task.supplier}</td>
                <td>{task.buyer}</td>
                <td>{task.dueDate}</td>
                <td>
                  <span className={`time-remaining ${task.isOverdue ? 'overdue' : task.timeRemaining === 'Today' ? 'today' : 'soon'}`}>
                    <ClockIcon />
                    {task.timeRemaining}
                  </span>
                </td>
                <td>
                  <div className="action-links">
                    {task.taskType === 'PENDING_INVOICE' ? (
                      <button 
                        className="action-link primary"
                        onClick={() => navigate(`/pending-invoices/${task.id}`)}
                      >
                        Review Invoice
                      </button>
                    ) : (
                      <>
                        <button 
                          className="action-link"
                          onClick={() => onSendRequest?.(task)}
                        >
                          Send Request
                        </button>
                        <span className="action-separator">|</span>
                        <button 
                          className="action-link primary"
                          onClick={() => onSearchGmail?.(task)}
                        >
                          Gmail
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CollapsibleSection>
  );
};

export default CriticalActionsSection;
